"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Workflow, Target, Lock } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { renderAccent } from "@/lib/render-accent";

const REASONS: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: ShieldCheck,
    title: "Certified in the Claude Partner Network",
    body: "We build on Claude and the Claude Agent SDK — not around it. The agent patterns come from Anthropic's own guidance, not a wrapper.",
  },
  {
    icon: Workflow,
    title: "Real tools, not a chatbot",
    body: "Agents that actually act — reading your documents, writing to your systems through MCP tools — with a human in the loop on every decision that matters.",
  },
  {
    icon: Target,
    title: "Proven before it ships",
    body: "Every agent is scored with Agent GPA evals against your own manual baseline. Nothing goes live until it beats a human on a golden set.",
  },
  {
    icon: Lock,
    title: "Regulated-ready, Malaysia & Singapore",
    body: "PDPA/PDPC-aligned data handling, auditable trails, and least-privilege access — built for BNM/MAS-governed work across the region.",
  },
];

// Text only. "OpenAI Select Partner" sits beside the Claude line as plain text;
// the hero carries the Claude Partner Network badge, so no OpenAI badge or logo
// may ever appear on this page.
const PROOF = [
  "Certified in the Claude Partner Network",
  "OpenAI Select Partner",
  "Malaysia Digital (MDEC) certified",
  "25+ projects delivered",
  "8+ years of engineering",
];

interface WhyAnchorSprintSectionProps {
  locale?: Locale;
}

export default function WhyAnchorSprintSection({
  locale = defaultLocale,
}: WhyAnchorSprintSectionProps) {
  const prefersReducedMotion = useReducedMotion();
  const inView = {
    initial: prefersReducedMotion ? false : { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  return (
    <section className="bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...inView} className="max-w-3xl">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Why Anchor Sprint")}
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {renderAccent(t(locale, "Agentic AI, built the [[honest way]]"))}
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(
              locale,
              "Anyone can demo an agent. We build ones that hold up in production — governed, evaluated, and honest about what they can and can't do.",
            )}
          </p>
        </motion.div>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {REASONS.map(({ icon: Icon, title, body }, i) => (
            <motion.div
              key={title}
              {...inView}
              transition={{ ...inView.transition, delay: i * 0.05 }}
              className="flex items-start gap-5 rounded-2xl border border-border bg-card p-6"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15">
                <Icon className="h-5 w-5" aria-hidden />
              </span>
              <div>
                <h3 className="font-display text-base font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-neutral-muted">
                  {t(locale, body)}
                  {/* Contextual anchor to the "Claude partner Malaysia" owner
                      page (2026-09-30), replacing a badge-only mention. */}
                  {i === 0 && (
                    <>
                      {" "}
                      {t(locale, "Details on our ")}
                      <Link
                        href={getLocalizedPathname(
                          "/partners/anthropic-claude-partner/",
                          locale,
                        )}
                        className="font-medium text-primary underline underline-offset-4"
                      >
                        {t(locale, "Claude partner in Malaysia")}
                      </Link>
                      {t(locale, " page.")}
                    </>
                  )}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Proof strip */}
        <motion.ul
          {...inView}
          className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-border pt-8"
        >
          {PROOF.map((item) => (
            <li
              key={item}
              className="flex items-center gap-2 text-sm font-medium text-neutral-muted"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-primary" aria-hidden />
              {t(locale, item)}
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
